import axios from 'axios';
import { server_url } from '../Settings'
import { exchangeIOLModelNames } from '../helpers/enums/IOLExchangeModels'
axios.defaults.withCredentials = true;

// IOL Exchange — calculation service.
//
// The mathematics lives in the backend engine, not here. That is deliberate:
// [F1]–[F6] are validated against Dr. Lombard's workbook by a fixture suite on
// the server side, and a second implementation in the browser would be a second
// thing to keep correct. This file only sends the form and reshapes the answer.
// The same goes for the toric block (Dr. Buonsanti's workbook): it is computed by
// the server, at the recommended power, when "Use toric?" is Yes or Not sure.
//
// Backend: POST /iolExchange/calculate  (toricaligner.iolExchange.IolExchangeController)

// Approximate validated power range (SRS 1.7 "Extrapolation"). Kept here so the
// UI can label rows without a round trip; the server flags the same band.
export const MIN_SAFE_POWER = 6;
export const MAX_SAFE_POWER = 27;

// Warning codes the engine returns as data (SRS 1.7). The UI decides the wording.
export const WARNING = {
    NO_ELIGIBLE_CANDIDATE: 'NO_ELIGIBLE_CANDIDATE',
    SULCUS_ADJUSTMENT_APPLIED: 'SULCUS_ADJUSTMENT_APPLIED',
    EXTRAPOLATED_POWER_RANGE: 'EXTRAPOLATED_POWER_RANGE',
    A_CONSTANT_RANGE_EXTRAPOLATED: 'A_CONSTANT_RANGE_EXTRAPOLATED',
    TORIC_POWER_OUTSIDE_TABLE: 'TORIC_POWER_OUTSIDE_TABLE',
    TORIC_CYLINDER_OUTSIDE_OPTIONS: 'TORIC_CYLINDER_OUTSIDE_OPTIONS',
};

// "Use toric for exchange?": Yes and Not sure both run the toric block. Not sure asks for
// the same data and shows the toric result next to the spherical one, so the surgeon
// decides after seeing both (Dr. Buonsanti, 2 Nov). The server reads the answer the same way.
export function usesToric(answer){
    return answer === 'Yes' || answer === 'Not sure';
}

function num(value){
    let parsed = parseFloat(value);
    return Number.isNaN(parsed)? null : parsed;
}

// Only a real model name is recorded; the selects start on placeholders.
function modelName(name){
    return exchangeIOLModelNames.includes(name)? name : null;
}

function text(value){
    return (value === "" || value === undefined)? null : value;
}

// Builds the request body from the form states held in IOLExchangeCalculator.js.
//
// The second group of fields does not take part in the calculation. The server stores
// them with the result (IolExchangeResultRecorder), so a stored case can be read back
// with everything the surgeon entered.
export function buildRequest({
    changeModel, currentPower, currentAConstant, currentPosition,
    sphere, cylinder, newAConstant, newPosition, targetMRSE,
    caseType, constantSource, currentModel, newModel, axis, reasons, otherReason, useToric,
    currentIolCylinder, currentIolAxis,
}){
    let fromModelList = (constantSource === 'Model list');
    return {
        changeIolModel: changeModel,
        currentAConstant: num(currentAConstant),
        // Only sent when the platform actually changes; otherwise [F1] contributes
        // nothing and the server treats the shift as zero (SRS 1.4).
        newAConstant: (changeModel)? num(newAConstant) : num(currentAConstant),
        currentIolPower: num(currentPower),
        sphere: num(sphere),
        // Blank cylinder means none. Sign is significant (SRS 1.1).
        cylinder: (cylinder === "" || cylinder === null || cylinder === undefined)? 0 : num(cylinder),
        targetMrse: num(targetMRSE),
        currentPosition: currentPosition,
        newPosition: newPosition,

        caseType: text(caseType),
        constantSource: text(constantSource),
        currentIolModel: (fromModelList)? modelName(currentModel) : null,
        newIolModel: (fromModelList)? modelName((changeModel)? newModel : currentModel) : null,
        axis: text(axis),
        reasons: (Array.isArray(reasons))? reasons : [],
        // The free text only counts when "Other" is ticked (SRS 1.3).
        otherReason: (Array.isArray(reasons) && reasons.includes('Other'))? text(otherReason) : null,
        useToric: text(useToric),
        // Toric block: only sent with useToric "Yes" or "Not sure", so values left in the
        // hidden fields are not stored with a spherical case. With no implanted cylinder
        // the server takes the refraction axis for the marks.
        currentIolCylinder: (usesToric(useToric))? num(currentIolCylinder) : null,
        currentIolAxis: (usesToric(useToric))? num(currentIolAxis) : null,
    };
}

// Reshapes the engine result into what Result.jsx renders.
//
// The engine returns BigDecimals serialised as JSON numbers, a 13-row ladder
// (empty when nothing qualifies — the workbook blanks it too), the full 32-rung
// modeled range, and structured warnings.
export function adaptResult(data, currentPower){
    let warnings = data.warnings || [];
    let ladder = data.ladder || [];

    return {
        actualPower: parseFloat(currentPower),
        // The "Actual" row: current MRSE after the A-constant shift, applied once
        // at the implanted power (SRS 1.5, [F1]).
        actualRefraction: data.anchorMrse,
        currentMRSE: data.currentMrse,
        // Named "modeled..." on the wire because a Java getter starting with two
        // capitals would serialise as "AConstantShift" — see IolExchangeResult.
        aConstantShift: data.modeledAConstantShift,

        recommendedPower: (data.recommendedPower === null || data.recommendedPower === undefined)?
            null : data.recommendedPower,
        recommendedMRSE: (data.recommendedMrse === null || data.recommendedMrse === undefined)?
            null : data.recommendedMrse,
        // For a bag→sulcus exchange the server also reports what the bag ladder
        // would have picked, so the surgeon can see the size of the adjustment.
        // Reference only — it is never a fallback (SRS 1.7).
        bagRecommendedPower: (data.bagRecommendedPower === null || data.bagRecommendedPower === undefined)?
            null : data.bagRecommendedPower,

        noEligibleCandidate: warnings.includes(WARNING.NO_ELIGIBLE_CANDIDATE),
        extrapolated: warnings.includes(WARNING.EXTRAPOLATED_POWER_RANGE),
        sulcusAdjusted: warnings.includes(WARNING.SULCUS_ADJUSTMENT_APPLIED),
        aConstantExtrapolated: warnings.includes(WARNING.A_CONSTANT_RANGE_EXTRAPOLATED),
        toricOutsideTable: warnings.includes(WARNING.TORIC_POWER_OUTSIDE_TABLE),
        toricOutsideOptions: warnings.includes(WARNING.TORIC_CYLINDER_OUTSIDE_OPTIONS),
        warnings: warnings,

        // Null unless a toric calculation was asked for and the recommended power is
        // inside the toric tables. Axes are null where the workbook writes "Sin eje".
        toric: (data.toric === null || data.toric === undefined)? null : {
            suggestedAxis: data.toric.suggestedAxis,
            axisIncrement: data.toric.axisIncrement,
            requiredCylinder: data.toric.requiredCylinder,
            adjustedCylinder: data.toric.adjustedCylinder,
            selectedCylinder: data.toric.selectedCylinder,
            options: (data.toric.options || []).map((option) => ({
                cylinder: option.cylinder,
                iolAxis: option.iolAxis,
                residualSphere: option.residualSphere,
                residualCylinder: option.residualCylinder,
                residualAxis: option.residualAxis,
                selected: option.selected,
            })),
        },

        rows: ladder.map((row) => ({
            power: row.power,
            bagEquivalentPower: row.bagEquivalentPower,
            predictedMRSE: row.predictedMrse,
            isActual: row.anchor,
            isRecommended: row.recommended,
            isExtrapolated: row.extrapolated,
        })),
    };
}

// Runs one exchange calculation.
// Resolves {ok: true, result} or {ok: false, error} — never throws, so the caller
// can show the failure in the results panel instead of leaving a dead button.
export function calculateExchange(formValues){
    return new Promise( resolve => {
        axios.post(server_url + "/iolExchange/calculate", buildRequest(formValues))
        .then(resp => {
            resolve({ok: true, result: adaptResult(resp.data, formValues.currentPower)});
        })
        .catch(function (error){
            let message = (error.response && error.response.status === 400)?
                "The server could not read these inputs. Check the current IOL power, refraction and target."
              : "The calculation service could not be reached. Check your connection and try again.";
            resolve({ok: false, error: message});
        })
    })
}
